import { useEffect, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useRuntimeConfig } from '@/config/useRuntimeConfig';
import { useAuth } from '@/auth/useAuth';
import {
    createOrgTenant,
    fetchOrgTenants,
    OrgAdminApiError,
    updateOrgTenant,
    type Organization,
    type OrgTenant,
    type OrgTenantCreated,
} from '@/api/orgAdmin';
import {
    DropdownMenu,
    EmptyState,
    ErrorState,
    Modal,
    SectionCard,
    SkeletonTable,
    useToast,
    type DropdownMenuItem,
} from '@/components/ui';

interface OrgTenantsTabProps {
    org: Organization;
    canManage: boolean;
    /** `?action=create` — the "Thêm cơ sở" affordance from the list's amber
     * "Chưa có cơ sở" state and the quick-peek drawer land here already open. */
    autoOpenCreate: boolean;
    onAutoOpenConsumed: () => void;
}

/**
 * EAA-1815: `/organizations/:id?tab=tenants` — full cơ sở management.
 *
 * Logic carried over from the pre-redesign `OrganizationDetailPage` almost
 * verbatim (create/deactivate/reactivate, the one-time API-token banner);
 * only the surrounding chrome changed. "Cấp cha" always reads the org's own
 * name — see `OrgOverviewTab`'s doc comment for why no real nested
 * tenant-under-tenant data exists to show here.
 */
export function OrgTenantsTab({
    org,
    canManage,
    autoOpenCreate,
    onAutoOpenConsumed,
}: OrgTenantsTabProps) {
    const { t } = useTranslation();
    const config = useRuntimeConfig();
    const { accessToken } = useAuth();
    const queryClient = useQueryClient();
    const toast = useToast();
    const orgId = org.id;

    const [tenantForm, setTenantForm] = useState({ name: '', slug: '', admin_email: '' });
    const [tenantModalOpen, setTenantModalOpen] = useState(false);
    const [tenantFormError, setTenantFormError] = useState<string | null>(null);
    const [pendingDeactivate, setPendingDeactivate] = useState<OrgTenant | null>(null);
    const [toggleError, setToggleError] = useState<string | null>(null);
    const [createdTenant, setCreatedTenant] = useState<OrgTenantCreated | null>(null);

    useEffect(() => {
        if (autoOpenCreate) {
            setTenantModalOpen(true);
            onAutoOpenConsumed();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [autoOpenCreate]);

    const tenantsQuery = useQuery({
        queryKey: ['organization-tenants', orgId, accessToken],
        queryFn: () => fetchOrgTenants(config.apiBase, accessToken as string, orgId),
        retry: false,
    });
    const tenants = tenantsQuery.data ?? [];

    const apiErrorMessage = (error: unknown, fallback: string) =>
        error instanceof OrgAdminApiError ? error.message : fallback;

    const createTenantMutation = useMutation({
        mutationFn: () =>
            createOrgTenant(config.apiBase, accessToken as string, orgId, {
                name: tenantForm.name.trim(),
                slug: tenantForm.slug.trim(),
                ...(tenantForm.admin_email.trim()
                    ? { admin_email: tenantForm.admin_email.trim() }
                    : {}),
            }),
        onSuccess: (data) => {
            setCreatedTenant(data);
            setTenantForm({ name: '', slug: '', admin_email: '' });
            setTenantFormError(null);
            setTenantModalOpen(false);
            toast.success(
                t('admin.organizations.tenantsTab.toast.created', {
                    name: data.tenant.name ?? data.tenant.slug,
                })
            );
            void queryClient.invalidateQueries({ queryKey: ['organization-tenants', orgId] });
        },
        onError: (error) => {
            setTenantFormError(
                apiErrorMessage(error, t('admin.organizations.tenantsTab.form.genericCreateError'))
            );
        },
    });

    const toggleTenantMutation = useMutation({
        mutationFn: (args: { tenantId: string; is_active: boolean }) =>
            updateOrgTenant(config.apiBase, accessToken as string, orgId, args.tenantId, {
                is_active: args.is_active,
            }),
        onSuccess: (_data, variables) => {
            setToggleError(null);
            setPendingDeactivate(null);
            toast.success(
                variables.is_active
                    ? t('admin.organizations.tenantsTab.toast.reactivated')
                    : t('admin.organizations.tenantsTab.toast.deactivated')
            );
            void queryClient.invalidateQueries({ queryKey: ['organization-tenants', orgId] });
        },
        onError: (error) => {
            setToggleError(
                apiErrorMessage(error, t('admin.organizations.tenantsTab.genericUpdateError'))
            );
        },
    });

    const onCreateTenant = (event: FormEvent) => {
        event.preventDefault();
        if (!tenantForm.name.trim() || !tenantForm.slug.trim()) {
            setTenantFormError(t('admin.organizations.tenantsTab.form.validationError'));
            return;
        }
        createTenantMutation.mutate();
    };

    const openTenantModal = () => {
        setTenantFormError(null);
        setTenantModalOpen(true);
    };
    const closeTenantModal = () => {
        if (createTenantMutation.isPending) return;
        setTenantModalOpen(false);
    };
    const closeDeactivateConfirm = () => {
        if (toggleTenantMutation.isPending) return;
        setPendingDeactivate(null);
        setToggleError(null);
    };

    return (
        <div className="portal-org-detail-main">
            <SectionCard
                title={t('admin.organizations.tenantsTab.sectionTitle', {
                    count: tenants.length,
                })}
                extra={
                    canManage && (
                        <button
                            type="button"
                            className="portal-btn portal-btn-primary"
                            onClick={openTenantModal}
                        >
                            {t('admin.organizations.tenantsTab.createButton')}
                        </button>
                    )
                }
            >
                {toggleError && <p className="portal-admin-error">{toggleError}</p>}
                {tenantsQuery.isLoading && (
                    <div role="status" aria-live="polite">
                        <span className="portal-sr-only">
                            {t('admin.organizations.loadingSrOnly')}
                        </span>
                        <SkeletonTable rows={3} columns={4} />
                    </div>
                )}
                {tenantsQuery.isError && (
                    <ErrorState
                        description={t('admin.organizations.tenantsTab.loadErrorDescription')}
                        onRetry={() => void tenantsQuery.refetch()}
                        retrying={tenantsQuery.isFetching}
                    />
                )}
                {tenantsQuery.isSuccess && tenants.length === 0 && (
                    <EmptyState
                        title={t('admin.organizations.tenantsTab.emptyTitle')}
                        description={t('admin.organizations.tenantsTab.emptyDescription')}
                        action={
                            canManage && (
                                <button
                                    type="button"
                                    className="portal-btn portal-btn-primary"
                                    onClick={openTenantModal}
                                >
                                    {t('admin.organizations.createTenantAction')}
                                </button>
                            )
                        }
                    />
                )}
                {tenants.length > 0 && (
                    <div className="portal-table-scroll">
                        <table className="portal-admin-table">
                            <thead>
                                <tr>
                                    <th>{t('admin.organizations.columnName')}</th>
                                    <th>{t('admin.organizations.columnParentLevel')}</th>
                                    <th>{t('admin.organizations.columnSlug')}</th>
                                    <th>{t('admin.organizations.columnStatus')}</th>
                                    {canManage && (
                                        <th>
                                            <span className="portal-sr-only">
                                                {t('common.actions')}
                                            </span>
                                        </th>
                                    )}
                                </tr>
                            </thead>
                            <tbody>
                                {tenants.map((tenant) => {
                                    const items: DropdownMenuItem[] = tenant.tenant_is_active
                                        ? [
                                              {
                                                  key: 'deactivate-tenant',
                                                  label: t('admin.organizations.deactivateAction'),
                                                  danger: true,
                                                  onSelect: () => setPendingDeactivate(tenant),
                                              },
                                          ]
                                        : [
                                              {
                                                  key: 'reactivate-tenant',
                                                  label: t('admin.organizations.reactivateAction'),
                                                  onSelect: () =>
                                                      toggleTenantMutation.mutate({
                                                          tenantId: tenant.tenant_id,
                                                          is_active: true,
                                                      }),
                                              },
                                          ];
                                    return (
                                        <tr key={tenant.id}>
                                            <td>{tenant.name ?? tenant.tenant_id}</td>
                                            <td style={{ color: 'var(--color-text-muted)' }}>
                                                {org.name}
                                            </td>
                                            <td>
                                                <code>{tenant.slug}</code>
                                            </td>
                                            <td>
                                                <span
                                                    className={
                                                        'portal-console-badge' +
                                                        (tenant.tenant_is_active ? ' is-on' : '')
                                                    }
                                                >
                                                    {tenant.tenant_is_active
                                                        ? t('admin.organizations.status.active')
                                                        : t('admin.organizations.status.inactive')}
                                                </span>
                                            </td>
                                            {canManage && (
                                                <td>
                                                    <DropdownMenu
                                                        items={items}
                                                        triggerAriaLabel={t(
                                                            'admin.organizations.tenantsTab.rowActionsAriaLabel',
                                                            { name: tenant.name ?? tenant.tenant_id }
                                                        )}
                                                    />
                                                </td>
                                            )}
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                )}

                {createdTenant?.api_token?.key && (
                    <div className="portal-admin-secret" role="alert">
                        <strong>{t('admin.organizations.tenantsTab.apiTokenBannerTitle')}</strong>
                        <code>{createdTenant.api_token.key}</code>
                        <button type="button" onClick={() => setCreatedTenant(null)}>
                            {t('admin.organizations.tenantsTab.apiTokenSavedButton')}
                        </button>
                    </div>
                )}
            </SectionCard>

            {canManage && (
                <Modal
                    open={tenantModalOpen}
                    onCancel={closeTenantModal}
                    title={t('admin.organizations.tenantsTab.createModal.title')}
                    footer={
                        <>
                            <button
                                type="button"
                                className="portal-btn"
                                onClick={closeTenantModal}
                                disabled={createTenantMutation.isPending}
                            >
                                {t('common.cancel')}
                            </button>
                            <button
                                type="submit"
                                form="create-tenant-form"
                                className="portal-btn portal-btn-primary"
                                disabled={createTenantMutation.isPending}
                            >
                                {createTenantMutation.isPending
                                    ? t('admin.organizations.tenantsTab.createModal.submitPending')
                                    : t('admin.organizations.createTenantAction')}
                            </button>
                        </>
                    }
                >
                    <form
                        id="create-tenant-form"
                        className="portal-admin-form"
                        onSubmit={onCreateTenant}
                    >
                        <label>
                            {t('admin.organizations.columnParentLevel')}
                            <input value={org.name} disabled />
                        </label>
                        <label>
                            {t('admin.organizations.tenantsTab.form.nameLabel')}
                            <input
                                value={tenantForm.name}
                                onChange={(e) =>
                                    setTenantForm({ ...tenantForm, name: e.target.value })
                                }
                                placeholder={t('admin.organizations.tenantsTab.form.namePlaceholder')}
                                required
                            />
                        </label>
                        <label>
                            {t('admin.organizations.columnSlug')}
                            <input
                                value={tenantForm.slug}
                                onChange={(e) =>
                                    setTenantForm({ ...tenantForm, slug: e.target.value })
                                }
                                placeholder={t('admin.organizations.tenantsTab.form.slugPlaceholder')}
                                required
                            />
                        </label>
                        <label>
                            {t('admin.organizations.tenantsTab.form.adminEmailLabel')}
                            <input
                                type="email"
                                value={tenantForm.admin_email}
                                onChange={(e) =>
                                    setTenantForm({ ...tenantForm, admin_email: e.target.value })
                                }
                                placeholder={t(
                                    'admin.organizations.tenantsTab.form.adminEmailPlaceholder'
                                )}
                            />
                        </label>
                        {tenantFormError && (
                            <p className="portal-admin-error">{tenantFormError}</p>
                        )}
                    </form>
                </Modal>
            )}

            <Modal
                open={pendingDeactivate !== null}
                onCancel={closeDeactivateConfirm}
                title={t('admin.organizations.tenantsTab.deactivateModal.title')}
                width={384}
                footer={
                    <>
                        <button
                            type="button"
                            className="portal-btn"
                            onClick={closeDeactivateConfirm}
                            disabled={toggleTenantMutation.isPending}
                        >
                            {t('common.cancel')}
                        </button>
                        <button
                            type="button"
                            className="portal-btn portal-btn-danger"
                            disabled={toggleTenantMutation.isPending}
                            onClick={() => {
                                if (!pendingDeactivate) return;
                                toggleTenantMutation.mutate({
                                    tenantId: pendingDeactivate.tenant_id,
                                    is_active: false,
                                });
                            }}
                        >
                            {toggleTenantMutation.isPending
                                ? t('admin.organizations.processingLabel')
                                : t('admin.organizations.deactivateAction')}
                        </button>
                    </>
                }
            >
                <p>
                    {t('admin.organizations.tenantsTab.deactivateModal.bodyPrefix')}{' '}
                    <strong>{pendingDeactivate?.name ?? pendingDeactivate?.tenant_id}</strong>{' '}
                    {t('admin.organizations.tenantsTab.deactivateModal.bodySuffix')}
                </p>
            </Modal>
        </div>
    );
}

export default OrgTenantsTab;
